import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Shield,
  Globe,
  CreditCard,
  Lock,
  Wallet,
  ShieldCheck,
  Fingerprint,
  Users,
  BadgeCheck,
  BarChart3,
  FileText,
  KeyRound,
} from 'lucide-react';
import {
  PageHero,
  EyebrowBadge,
  SectionHeader,
  MarketingSection,
  MKT_CONTAINER,
  MKT_GRID_GAP,
  MKT_CARD,
} from '../../components/public/marketing';
import { useBrand } from '../../hooks/useBrand';

const BENEFITS = [
  { key: 'b1', icon: Globe },
  { key: 'b2', icon: Wallet },
  { key: 'b3', icon: BarChart3 },
  { key: 'b4', icon: ShieldCheck },
];

const TRUST_SIGNALS = [
  { key: 'trust_custody', icon: Wallet },
  { key: 'trust_ledger', icon: FileText },
  { key: 'trust_sec_encrypt', icon: Lock },
  { key: 'trust_sec_hmac', icon: KeyRound },
  { key: 'trust_sec_isolation', icon: ShieldCheck },
  { key: 'trust_sec_kyc', icon: Fingerprint },
];

const STEPS = [
  { key: 'step1', icon: Users },
  { key: 'step2', icon: BadgeCheck },
  { key: 'step3', icon: CreditCard },
];

/** Shared primary CTA — used once on the page (hero only). */
const PRIMARY_CTA_CLASS =
  'group inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-blue-600/40 sm:w-auto sm:px-10 sm:py-4 sm:text-base';

/** De-emphasized secondary navigation — text links only. */
const SECONDARY_LINK_CLASS =
  'text-sm text-gray-500 underline-offset-4 transition hover:text-gray-300 hover:underline';

export default function HomePage() {
  const { t } = useTranslation();
  const brand = useBrand();

  return (
    <>
      {/* Hero — dominant visual + sole strong CTA */}
      <PageHero
        variant="home"
        badge={
          <EyebrowBadge tone="blue">
            {t('home.badge_simple')}
          </EyebrowBadge>
        }
        title={
          <>
            {t('home.hero_line1')}{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {t('home.hero_highlight')}
            </span>
          </>
        }
        subtitle={t('home.hero_subtitle_short')}
      >
        <Link to="/register" className={PRIMARY_CTA_CLASS}>
          {t('home.cta_primary')}
          <ArrowRight size={18} className="transition group-hover:translate-x-0.5" />
        </Link>

        <p className="mt-4 text-xs text-gray-500 sm:text-sm">{t('home.hero_trust_line')}</p>

        <p className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <Link to="/pricing" className={SECONDARY_LINK_CLASS}>
            {t('nav.pricing')}
          </Link>
          <span className="text-gray-600" aria-hidden>
            ·
          </span>
          <Link to="/documentation" className={SECONDARY_LINK_CLASS}>
            {t('nav.docs')}
          </Link>
        </p>
      </PageHero>

      {/* Benefits — outcome-only, no technical stacking */}
      <MarketingSection tone="muted" tight>
        <div className={MKT_CONTAINER}>
          <SectionHeader title={t('home.benefits_title')} subtitle={t('home.benefits_subtitle')} />
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${MKT_GRID_GAP}`}>
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.key} className={MKT_CARD}>
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] sm:mb-4">
                    <Icon size={18} className="text-gray-400" />
                  </div>
                  <h3 className="mb-1 text-base font-semibold text-white sm:text-lg">
                    {t(`home.${b.key}_title`)}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-400 sm:text-sm">
                    {t(`home.${b.key}_desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </MarketingSection>

      {/* Trust — factual signals, no metrics or promotional claims */}
      <MarketingSection tight>
        <div className={`${MKT_CONTAINER} max-w-3xl`}>
          <SectionHeader
            align="left"
            eyebrow={
              <EyebrowBadge tone="green">
                <Shield size={12} className="text-green-400" />
                {t('home.trust_eyebrow')}
              </EyebrowBadge>
            }
            title={t('home.trust_title')}
            subtitle={t('home.trust_subtitle', { brandFull: brand.fullName })}
          />
          <ul className="space-y-2.5">
            {TRUST_SIGNALS.map((item) => {
              const Icon = item.icon;
              return (
                <li
                  key={item.key}
                  className="flex items-start gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3"
                >
                  <Icon size={16} className="mt-0.5 shrink-0 text-gray-500" />
                  <span className="text-sm leading-relaxed text-gray-400">{t(`home.${item.key}`)}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-5 text-xs leading-relaxed text-gray-500 sm:text-sm">
            {t('home.trust_footnote')}{' '}
            <Link to="/documentation" className={SECONDARY_LINK_CLASS}>
              {t('nav.docs')}
            </Link>
          </p>
        </div>
      </MarketingSection>

      {/* How it works — minimal, non-technical */}
      <MarketingSection tone="muted" tight>
        <div className={MKT_CONTAINER}>
          <SectionHeader title={t('home.steps_simple_title')} subtitle={t('home.steps_simple_subtitle')} />
          <div className={`grid grid-cols-1 md:grid-cols-3 ${MKT_GRID_GAP}`}>
            {STEPS.map((item, idx) => {
              const Icon = item.icon;
              const stepNum = String(idx + 1).padStart(2, '0');
              return (
                <div key={item.key} className="text-center">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] sm:mb-4">
                    <Icon size={20} className="text-gray-400" />
                  </div>
                  <div className="mb-1 text-[10px] font-medium tracking-widest text-gray-500 sm:text-xs">
                    {stepNum}
                  </div>
                  <h3 className="mb-1 text-base font-medium text-white sm:text-lg">
                    {t(`home.${item.key}_simple_title`)}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-500 sm:text-sm">
                    {t(`home.${item.key}_simple_desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </MarketingSection>

      {/* Closing — reinforces action without a competing button */}
      <MarketingSection tight>
        <div className={`${MKT_CONTAINER} max-w-2xl text-center`}>
          <h2 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
            {t('home.final_cta_title')}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-gray-500 sm:text-base">
            {t('home.final_cta_subtitle', { brandFull: brand.fullName })}
          </p>
          <Link
            to="/register"
            className={`${SECONDARY_LINK_CLASS} mt-5 inline-flex items-center gap-1.5 font-medium text-gray-400`}
          >
            {t('home.cta_primary')}
            <ArrowRight size={14} />
          </Link>
          <p className="mt-4 text-xs text-gray-600">{t('home.final_cta_note')}</p>
        </div>
      </MarketingSection>
    </>
  );
}
